import { useEffect, useId, useState } from "react";
import {
  parameterProblem,
  parametersBody,
  parameterTexts,
  type ServiceParameterForm,
} from "@shared-core/domain/service-parameters";
import { ServiceParameterFields } from "../orders/ServiceParameterFields";
import type {
  CustomWidget,
  CustomWidgetProps,
} from "../../patterns/command-shell/types";

/** The body path the widget owns: the service's parameters, by key. */
const PATH = "parameters";

/** What the dry run says the chosen service asks for now; null until it has said. */
function parametersAskedFor(
  props: CustomWidgetProps,
): ServiceParameterForm[] | null {
  const asked = props.hints[PATH]?.widget_context?.parameters;
  return Array.isArray(asked) ? (asked as ServiceParameterForm[]) : null;
}

/**
 * The parameters widget (One E spec 13 §4.5 row 28, Add extra): the
 * parameters the chosen service and its ticked activities ask for, drawn as
 * Classic's dialog draws them (`ServiceParameterFields`). It sends only those
 * asked for, numbers as numbers: a parameter no longer asked for (its activity
 * unticked, another service chosen) leaves the body. A box holding something
 * that isn't a whole number says so beside it and sends nothing; every other
 * refusal is the dry run's, on `parameters.<key>`.
 */
function ServiceParameters(props: CustomWidgetProps) {
  const { body, errors, disabled, setValues } = props;
  const id = useId();
  const asked = parametersAskedFor(props);
  const [texts, setTexts] = useState<Record<string, string>>(() =>
    parameterTexts(body[PATH]),
  );

  // What the body holds that the service no longer asks for is dropped (the dictionary refuses it).
  const sent = JSON.stringify(body[PATH] ?? {});
  useEffect(() => {
    if (!asked) return;
    const keys = new Set(asked.map((p) => p.key));
    const held = parameterTexts(JSON.parse(sent));
    if (Object.keys(held).every((key) => keys.has(key))) return;
    setValues({ [PATH]: parametersBody(asked, held) });
  }, [asked, sent, setValues]);

  if (!asked || asked.length === 0) return null;

  const change = (key: string, value: string, isTyping: boolean) => {
    const next = { ...texts, [key]: value };
    setTexts(next);
    setValues({ [PATH]: parametersBody(asked, next) }, { debounce: isTyping });
  };
  const errorOf = (key: string) => {
    const parameter = asked.find((p) => p.key === key)!;
    const typed = texts[key];
    // A number the box can't send is said here; anything sent is the dry run's to judge.
    if (typed && parameter.value_type === "integer") {
      const local = parameterProblem(parameter, typed);
      if (local && !(key in parametersBody([parameter], { [key]: typed })))
        return local;
    }
    const served = errors.filter((e) => e.field === `${PATH}.${key}`);
    return served.length > 0
      ? served.map((e) => e.message).join(" ")
      : undefined;
  };
  const general = errors.filter(
    (e) => !asked.some((p) => e.field === `${PATH}.${p.key}`),
  );

  return (
    <section aria-label="Details" className="space-y-3">
      <ServiceParameterFields
        idPrefix={id}
        parameters={asked}
        values={texts}
        errorOf={errorOf}
        onChange={change}
        disabled={disabled}
      />
      {general.length > 0 && (
        <p className="text-sm text-red-600">
          {general.map((e) => e.message).join(" ")}
        </p>
      )}
    </section>
  );
}

export const serviceParametersWidget: CustomWidget = {
  ownedPaths: [PATH],
  Component: ServiceParameters,
  placement: "after_choices",
};

export { ServiceParameters as ServiceParametersWidget };
